import { AutoAssignType } from '../bucket/api/bucket-types';
import { useBudgetSummary } from '../budget/budget-store';
import { toDollars, toPennies, toPercentage } from '../utils/format';
import { DateTime } from 'luxon';
/**
 * Determines if a bucket is under-assigned based on its target settings.
 * A bucket is considered under-assigned if it:
 * 1. Has auto-assign enabled
 * 2. Is of type TARGET
 * 3. Has a target date set
 * 4. Has a positive shortfall amount needed to reach the target
 *
 * @param bucket - The bucket to check for under-assignment.
 * @param yearMonth - The year-month string in "YYYY-MM" format.
 * @returns true if the bucket needs more funds to meet its target, false otherwise.
 */
export function useIsTargetUnderAssigned(bucket, yearMonth) {
    const result = useAutoAssignmentTarget(bucket, yearMonth);
    // Early return if no valid target configuration
    const autoAssign = bucket.autoAssign;
    if (!autoAssign || autoAssign.disabled || autoAssign.type !== AutoAssignType.TARGET || !autoAssign.target) {
        return false;
    }
    return result.shortfall > 0;
}
/**
 * Calculates the target assignment details for a bucket using the current budget state.
 * This is a convenience wrapper around calculateTargetAssignment that pulls the required
 * parameters from the global budget state (year, month, budget summary).
 *
 * @param bucket - The bucket to calculate target assignment for.
 * @param yearMonth - The year-month string in "YYYY-MM" format.
 * @returns A RollingTargetResult containing shortfall, ideal monthly amount,
 *          months remaining, next target date, and progress percentage.
 */
export function useAutoAssignmentTarget(bucket, yearMonth) {
    const [year, month] = yearMonth.split('-').map(Number);
    const budgetSummary = useBudgetSummary();
    const bucketSummary = budgetSummary.summaryByMonth.get(yearMonth)?.bucketSummaryById.get(bucket.id);
    return calculateTargetAssignment({
        targetAmount: bucket.autoAssign?.amount || 0,
        targetDate: bucket.autoAssign?.target || '',
        currentMonth: month,
        currentYear: year,
        assigned: bucketSummary?.assigned ?? 0,
        leftover: bucketSummary?.leftover ?? 0,
        outflow: bucketSummary?.outflow ?? 0,
        available: bucketSummary?.available ?? 0,
        recurrenceMonths: bucket.autoAssign?.recurrenceMonths || 0,
    });
}
/**
 * Calculates the target assignment amount and related information for envelope budgeting.
 *
 * @param params The calculation parameters
 * @param params.targetAmount The target amount to reach
 * @param params.targetDate The target date to reach the amount by
 * @param params.recurrenceMonths How many months between target dates
 * @param params.currentMonth Current month (1-12)
 * @param params.currentYear Current year
 * @param params.leftover Amount leftover from previous period
 * @param params.assigned Amount already assigned this period
 * @param params.outflow Amount spent this period
 * @param params.available Current available amount
 * @returns Object containing shortfall, ideal monthly amount, months remaining, next target date and progress
 */
export function calculateTargetAssignment(params) {
    const { targetAmount, targetDate, recurrenceMonths, currentMonth, currentYear, leftover, assigned, outflow, available, } = params;
    const { nextTargetDate, monthsRemaining } = getNextTargetDateWithMonthsRemaining(recurrenceMonths, currentMonth, currentYear, targetDate);
    // if the target date is already over, there is nothing to calculate
    if (monthsRemaining <= 0) {
        return {
            shortfall: 0,
            idealMonthly: 0,
            monthsRemaining: 0,
            nextTargetDate,
            progress: toPercentage(available, targetAmount),
        };
    }
    // convert all amounts to pennies
    const targetPennies = toPennies(targetAmount);
    const leftoverPennies = toPennies(leftover);
    const outflowPennies = toPennies(outflow);
    const assignedPennies = toPennies(assigned);
    const availablePennies = toPennies(available);
    // Calculate what we need based on our starting position
    const currentPositionPennies = leftoverPennies - outflowPennies;
    const totalNeededPennies = Math.max(0, targetPennies - currentPositionPennies);
    if (totalNeededPennies === 0 && assignedPennies >= 0) {
        return {
            shortfall: 0,
            idealMonthly: 0,
            monthsRemaining,
            nextTargetDate,
            progress: 100,
        };
    }
    const idealMonthlyPennies = Math.floor(totalNeededPennies / monthsRemaining);
    // If we've already assigned enough to reach the target
    if (availablePennies >= targetPennies) {
        return {
            shortfall: 0,
            idealMonthly: toDollars(idealMonthlyPennies),
            monthsRemaining,
            nextTargetDate,
            progress: toPercentage(available, targetAmount),
        };
    }
    // Otherwise, calculate how much more is needed
    const shortfallPennies = assignedPennies < 0
        ? Math.min(targetPennies, idealMonthlyPennies + Math.abs(assignedPennies))
        : Math.max(0, idealMonthlyPennies - assignedPennies);
    return {
        shortfall: toDollars(shortfallPennies),
        idealMonthly: toDollars(idealMonthlyPennies),
        monthsRemaining,
        nextTargetDate,
        progress: toPercentage(available, targetAmount),
    };
}
/**
 * Calculates the next occurrence of a target date based on recurrence months
 * and returns the months remaining until that date.
 *
 * If recurrenceMonths is 0 (non-recurring target), returns the original target date.
 * For recurring targets, if the target date is on/before current month/year,
 * increments by recurrence months until a future date is found.
 *
 * @param recurrenceMonths - Number of months between recurrences (0 for non-recurring).
 * @param currentMonth - Current month (1-12).
 * @param currentYear - Current year.
 * @param targetDate - Target date in ISO format (YYYY-MM-DD).
 * @returns Object containing next target date and months remaining.
 */
export function getNextTargetDateWithMonthsRemaining(recurrenceMonths, currentMonth, currentYear, targetDate) {
    // Create the current date using the 1st of the current month/year
    const currentDate = DateTime.fromObject({ year: currentYear, month: currentMonth, day: 1 });
    // Parse the target date (assumed to be the 1st of its month)
    let nextDate = DateTime.fromISO(targetDate);
    // For recurring targets, increment until we find a future date
    if (recurrenceMonths > 0) {
        while (nextDate <= currentDate) {
            nextDate = nextDate.plus({ months: recurrenceMonths });
        }
    }
    // Calculate the total months difference
    const monthsRemaining = Math.max(0, (nextDate.year - currentDate.year) * 12 + (nextDate.month - currentDate.month));
    return {
        nextTargetDate: nextDate.toISODate(),
        monthsRemaining,
    };
}
